/**
 * SunoStyleButton — пикер стиля песни.
 *
 * Два режима:
 *   • Обычный (pill в .gm__pills / home): pill → `SunoStyleSheet` (модалка
 *     с буфером и «Применить») — как было всегда.
 *   • Section-режим (внутри панели инструментов, BbarSectionMode): строка
 *     «Стиль ›» → drill-in сабменю (Artlist-style) с ТЕМ ЖЕ SunoStyleEditor
 *     (жанры + свой инпут + «Улучшить через AI» + исключения) — функционал
 *     1:1, применение живое (без кнопки «Применить»).
 */
import { useContext, useRef, useState } from 'react'
import SunoStyleSheet from './SunoStyleSheet'
import SunoStyleEditor from './SunoStyleEditor'
import ToolsSubmenu from './ToolsSubmenu'
import { BbarSectionMode } from './bbarSectionContext'
import { Icon } from '../../../../public/icons/Icon.tsx'
import type { Model } from '../../../data/models'
import type { AudioOperation } from '../../../context/GenerateContext'

interface Props {
	model: Model | null
	currentStyle: string
	currentNegativeTags: string
	onApply: (result: { style: string; negativeTags: string }) => void
	disabled?: boolean
	/** Текущая параметрическая операция — меняет смысл поля "стиль":
	 *  - add_instrumental → "Описание инструментала" (отправится как `tags`)
	 *  - cover            → "Новый стиль" (перепевка)
	 *  - остальные        → просто "Стиль" */
	audioOperation?: AudioOperation
}

/** Контекст использования стиля — влияет на лейбл + placeholder. */
function labelsFor(op?: AudioOperation): { pillDefault: string; title: string } {
	switch (op) {
		case 'add_instrumental':
			return { pillDefault: 'Опиши инструментал', title: 'Описание инструментала' }
		case 'cover':
			return { pillDefault: 'Новый стиль', title: 'В какой стиль перепеть' }
		case 'add_vocals':
			return { pillDefault: 'Стиль вокала', title: 'Стиль для вокала' }
		case 'extend':
			return { pillDefault: 'Стиль', title: 'Стиль продолжения' }
		default:
			return { pillDefault: 'Pop', title: 'Настроить стиль песни' }
	}
}

/** Suno API лимит на длину style для текущей операции. Расхождение:
 *  generate / extend → 1000; add_vocals / add_instrumental → 500.
 *  Frontend hard-caps через maxLength TagsField чтобы избежать silent
 *  truncation на бэке. Совпадает с suno.py: see add_vocals/add_instrumental. */
function styleMaxLengthFor(op?: AudioOperation): number {
	switch (op) {
		case 'add_vocals':
		case 'add_instrumental':
			return 500
		default:
			return 1000
	}
}

export default function SunoStyleButton({
	model,
	currentStyle,
	currentNegativeTags,
	onApply,
	disabled,
	audioOperation,
}: Props) {
	const [open, setOpen] = useState(false)
	const asSection = useContext(BbarSectionMode)
	const rowRef = useRef<HTMLButtonElement>(null)
	const { pillDefault, title } = labelsFor(audioOperation)
	const styleMaxLength = styleMaxLengthFor(audioOperation)
	const pillLabel = currentStyle.trim() || pillDefault

	// ── Section-режим: строка «Стиль ›» + drill-in сабменю ──────────
	// Тот же SunoStyleEditor, что и в шторке; изменения применяются СРАЗУ
	// (onApply на каждый change) — сабменю без буфера, как Artlist.
	if (asSection) {
		return (
			<div className="bbar-section bbar-section--nav">
				<button
					ref={rowRef}
					type="button"
					className={`bbar-navrow${open ? ' bbar-navrow--open' : ''}`}
					onClick={() => setOpen((v) => !v)}
					disabled={disabled}
					aria-expanded={open}
				>
					<span className="bbar-navrow__icon" aria-hidden="true">
						<Icon id="gen-version" size={14} />
					</span>
					<span className="bbar-navrow__label">Стиль</span>
					<span className="bbar-navrow__value">{pillLabel}</span>
					<span className="bbar-navrow__chevron" aria-hidden="true" />
				</button>

				<ToolsSubmenu
					open={open}
					anchorRef={rowRef}
					onClose={() => setOpen(false)}
					width={380}
					className="gm-tools-submenu--style"
				>
					<div className="gm-tools-submenu__title">{title}</div>
					<div className="gm-tools-submenu__body">
						<SunoStyleEditor
							model={model}
							style={currentStyle}
							negativeTags={currentNegativeTags}
							onStyleChange={(v) => onApply({ style: v, negativeTags: currentNegativeTags })}
							onNegativeTagsChange={(v) => onApply({ style: currentStyle, negativeTags: v })}
							styleMaxLength={styleMaxLength}
						/>
					</div>
				</ToolsSubmenu>
			</div>
		)
	}

	return (
		<>
			<button
				type="button"
				className={`bbar-pill suno-style-btn${disabled ? ' bbar-pill--disabled' : ''}`}
				onClick={() => setOpen(true)}
				disabled={disabled}
				title={title}
			>
				<span className="bbar-pill__icon" aria-hidden="true">
					<Icon id="gen-version" size={14} />
				</span>
				<span className="bbar-pill__label">{pillLabel}</span>
			</button>

			<SunoStyleSheet
				open={open}
				onClose={() => setOpen(false)}
				model={model}
				currentStyle={currentStyle}
				currentNegativeTags={currentNegativeTags}
				onApply={onApply}
				title={title}
				styleMaxLength={styleMaxLength}
			/>
		</>
	)
}
